import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import useSEOMeta from '@/hooks/useSEOMeta';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const FEATURED_SLUGS = [
  '2025-new-year-greeting',
  'new-drama-role-announcement',
  '2024-summer-photobook-release',
];

const HIGHLIGHT_SLUGS = [
  'youtube-channel-1m-subscribers',
  'music-single-winter-ballad',
  'tokyo-girls-collection-2024',
  'fashion-magazine-cover-october',
];

export default function Home() {
  useSEOMeta({
    title: '三上悠亚 - 官方粉丝资讯与作品全览',
    description: '三上悠亚是日本知名女演员与偶像，本页汇集三上悠亚的最新动态、作品资讯、个人经历及粉丝必知内容，带你全面了解三上悠亚的演艺世界与魅力所在。',
    keywords: '三上悠亚,三上悠亚作品,三上悠亚最新动态,三上悠亚个人资料,三上悠亚粉丝站,三上悠亚写真,三上悠亚偶像',
    canonicalUrl: 'https://cdrmjj.cn/',
    ogType: 'website',
  });

  const featuredArticles = FEATURED_SLUGS.map((slug) => articles.find((a) => a.slug === slug)).filter(Boolean) as typeof articles;
  const highlightArticles = HIGHLIGHT_SLUGS.map((slug) => articles.find((a) => a.slug === slug)).filter(Boolean) as typeof articles;
  const latestArticles = articles.filter((a) => ![...FEATURED_SLUGS, ...HIGHLIGHT_SLUGS].includes(a.slug)).slice(0, 6);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '三上悠亚粉丝站',
    url: 'https://cdrmjj.cn/',
    description: '三上悠亚官方粉丝资讯与作品全览，汇集最新动态、作品资讯、个人经历及粉丝必知内容。',
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '三上悠亚粉丝站',
    url: 'https://cdrmjj.cn/',
    description: '三上悠亚官方粉丝资讯平台',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20soft%20minimalist%20abstract%20art%20background%20with%20warm%20coral%20pink%20and%20cream%20gradients%2C%20delicate%20organic%20shapes%2C%20Japanese%20aesthetic%20inspired%2C%20dreamy%20atmospheric%20lighting%2C%20subtle%20texture%2C%20editorial%20quality%2C%20large%20negative%20space&width=1800&height=900&seq=mikami-hero-01&orientation=landscape"
              alt="三上悠亚粉丝站"
              title="三上悠亚 - 官方粉丝资讯与作品全览"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight tracking-wide">
                三上悠亚
              </h1>
              <p className="mt-4 md:mt-6 text-base md:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed font-body">
                日本知名女演员与偶像，汇集最新动态、作品资讯、个人经历及粉丝必知内容，<br className="hidden md:block" />带你全面了解三上悠亚的演艺世界与魅力所在。
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/latest-news"
                  className="px-8 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-full text-sm font-label font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  浏览最新动态
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white rounded-full text-sm font-label font-medium backdrop-blur-sm transition-all duration-300 whitespace-nowrap cursor-pointer border border-white/20"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-10 relative z-20">
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-accent-50 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <span className="text-xs font-label text-foreground-700 group-hover:text-accent-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  精选推荐
                </h2>
                <p className="mt-1 text-sm text-foreground-500">编辑精选的热门内容</p>
              </div>
              <Link
                to="/category/latest-news"
                className="text-sm text-accent-500 hover:text-accent-600 font-label whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>

          {/* Highlight Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  热门资讯
                </h2>
                <p className="mt-1 text-sm text-foreground-500">粉丝最关注的内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {highlightArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>

          {/* Latest Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  最新发布
                </h2>
                <p className="mt-1 text-sm text-foreground-500">持续更新中</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>

          {/* Newsletter */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-20">
            <div className="bg-accent-500/8 rounded-2xl border border-accent-200/40 p-10 md:p-14 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-mail-send-line text-2xl"></i>
              </div>
              <h2 className="mt-5 text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                订阅最新资讯
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto leading-relaxed">
                第一时间获取三上悠亚的最新动态、作品发布与粉丝活动信息。
              </p>
              <form
                data-readdy-form=""
                action="https://readdy.ai/api/form/d9ssv658o1vn02ekulqg"
                method="POST"
                className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="请输入您的邮箱地址"
                  required
                  className="w-full sm:flex-1 px-4 py-3 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                />
                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-full text-sm font-label font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  立即订阅
                </button>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-form-hp"
                />
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}